/**
 * Product Variant Table JavaScript
 * - Handles size quantity inputs
 * - Calculates tier pricing and totals
 * - Adds products to inquiry cart via AJAX
 * - Updates UI dynamically
 */

class ProductVariantTable {
  constructor(element) {
    this.table = element;
    this.productId = element.dataset.productId;
    this.productTitle = element.dataset.productTitle;
    this.productCategory = element.dataset.productCategory || '';
    this.productType = element.dataset.productType || '';
    this.productImage = element.dataset.productImage || '';
    this.tierPricing = JSON.parse(element.dataset.tierPricing || '[]');
    
    // DOM elements
    this.sizeInputs = Array.from(element.querySelectorAll('.size-input'));
    this.tierBadgesContainer = element.querySelector('[data-tier-badges]');
    this.totalSummary = element.querySelector('[data-total-summary]');
    this.addToCartButton = element.querySelector('[data-add-to-cart]');
    
    // Summary elements
    this.totalPairsEl = element.querySelector('[data-testid="text-total-pairs"]');
    this.unitPriceEl = element.querySelector('[data-testid="text-unit-price"]');
    this.totalPriceEl = element.querySelector('[data-testid="text-total-price"]');
    
    // State
    this.quantities = {};
    this.totalPairs = 0;
    this.unitPrice = 0;
    this.totalPrice = 0;
    this.currentTier = null;
    
    this.init();
  }
  
  init() {
    // Initialize quantities
    this.sizeInputs.forEach(input => {
      const size = input.dataset.size;
      this.quantities[size] = 0;
    });
    
    // Render tier badges
    this.renderTierBadges();
    
    // Attach event listeners
    this.attachEventListeners();
    
    // Initial calculation
    this.calculate();
  }
  
  attachEventListeners() {
    // Quantity input changes
    this.sizeInputs.forEach(input => {
      input.addEventListener('input', (e) => {
        const size = e.target.dataset.size;
        const value = parseInt(e.target.value) || 0;
        this.quantities[size] = Math.max(0, value);
        this.calculate();
      });
      
      // Prevent negative values
      input.addEventListener('blur', (e) => {
        if (parseInt(e.target.value) < 0) {
          e.target.value = 0;
        }
      });
    });
    
    // Add to cart button
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', () => {
        this.addToCart();
      });
    }
  }
  
  calculate() {
    // Calculate total pairs
    this.totalPairs = Object.values(this.quantities).reduce((sum, qty) => sum + qty, 0);
    
    // Calculate unit price based on tier
    this.unitPrice = this.calculateTierPrice(this.totalPairs);
    
    // Calculate total price
    this.totalPrice = this.totalPairs * this.unitPrice;
    
    // Get current tier
    this.currentTier = this.getCurrentTier(this.totalPairs);
    
    // Update UI
    this.updateUI();
  }
  
  calculateTierPrice(totalPairs) {
    if (totalPairs === 0) return 0;
    
    const tier = this.tierPricing.find(t => {
      if (t.max === null) return totalPairs >= t.min;
      return totalPairs >= t.min && totalPairs <= t.max;
    });
    
    return tier ? tier.price : (this.tierPricing[0]?.price || 0);
  }
  
  getCurrentTier(totalPairs) {
    if (totalPairs === 0) return null;
    
    return this.tierPricing.find(t => {
      if (t.max === null) return totalPairs >= t.min;
      return totalPairs >= t.min && totalPairs <= t.max;
    }) || null;
  }
  
  updateUI() {
    // Update summary values
    if (this.totalPairsEl) {
      this.totalPairsEl.textContent = this.totalPairs;
    }
    
    if (this.unitPriceEl) {
      this.unitPriceEl.textContent = `$${this.unitPrice.toFixed(2)}/pair`;
    }
    
    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = `$${this.totalPrice.toFixed(2)}`;
    }
    
    // Show/hide total summary
    if (this.totalSummary) {
      this.totalSummary.style.display = this.totalPairs > 0 ? 'block' : 'none';
    }
    
    // Update tier badges
    this.updateTierBadges();
    
    // Enable/disable add to cart button
    if (this.addToCartButton) {
      this.addToCartButton.disabled = this.totalPairs === 0;
    }
  }
  
  renderTierBadges() {
    if (!this.tierBadgesContainer) return;
    
    this.tierBadgesContainer.innerHTML = this.tierPricing.map((tier, index) => {
      const maxLabel = tier.max === null ? '∞' : tier.max;
      return `
        <div class="tier-badge" 
             data-tier-index="${index}"
             data-testid="badge-tier-${index}">
          ${tier.min}-${maxLabel} pairs: $${tier.price.toFixed(2)}
        </div>
      `;
    }).join('');
  }
  
  updateTierBadges() {
    if (!this.tierBadgesContainer) return;
    
    const badges = this.tierBadgesContainer.querySelectorAll('.tier-badge');
    badges.forEach((badge, index) => {
      const isActive = this.currentTier && 
                      this.tierPricing[index] === this.currentTier;
      badge.classList.toggle('active', isActive);
    });
  }
  
  getSizeQuantities() {
    return Object.entries(this.quantities)
      .filter(([_, qty]) => qty > 0)
      .map(([size, quantity]) => ({ size, quantity }));
  }
  
  async addToCart() {
    if (this.totalPairs === 0) return;
    
    const sizeQuantities = this.getSizeQuantities();
    
    const cartItem = {
      productId: this.productId,
      productName: this.productTitle,
      category: this.productCategory,
      type: this.productType,
      sizeQuantities: sizeQuantities,
      totalPairs: this.totalPairs,
      unitPrice: this.unitPrice,
      totalPrice: this.totalPrice,
      image: this.productImage,
      timestamp: Date.now()
    };
    
    try {
      // Add to localStorage cart (inquiry cart)
      this.addToInquiryCart(cartItem);
      
      // Show success toast/message
      this.showSuccessMessage();
      
      // Reset form
      this.resetForm();
      
      // Update cart badge
      this.updateCartBadge();
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showErrorMessage();
    }
  }
  
  addToInquiryCart(item) {
    // Get existing cart from localStorage
    let cart = [];
    try {
      const cartData = localStorage.getItem('inquiryCart');
      if (cartData) {
        cart = JSON.parse(cartData);
      }
    } catch (e) {
      console.error('Error reading cart:', e);
    }
    
    // Add new item
    cart.push(item);
    
    // Save back to localStorage
    localStorage.setItem('inquiryCart', JSON.stringify(cart));
  }
  
  showSuccessMessage() {
    // Create toast notification
    const toast = document.createElement('div');
    toast.className = 'cart-toast cart-toast-success';
    toast.innerHTML = `
      <div class="cart-toast-content">
        <strong>Added to Inquiry Cart</strong>
        <p>${this.totalPairs} pairs of ${this.productTitle} added to your inquiry.</p>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    // Show toast
    setTimeout(() => toast.classList.add('show'), 10);
    
    // Remove toast after 3 seconds
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }
  
  showErrorMessage() {
    const toast = document.createElement('div');
    toast.className = 'cart-toast cart-toast-error';
    toast.innerHTML = `
      <div class="cart-toast-content">
        <strong>Error</strong>
        <p>Failed to add item to cart. Please try again.</p>
      </div>
    `;
    
    document.body.appendChild(toast);
    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }
  
  resetForm() {
    // Reset all inputs
    this.sizeInputs.forEach(input => {
      input.value = 0;
      const size = input.dataset.size;
      this.quantities[size] = 0;
    });
    
    // Recalculate
    this.calculate();
  }
  
  updateCartBadge() {
    // Update cart badge count
    try {
      const cartData = localStorage.getItem('inquiryCart');
      const cart = cartData ? JSON.parse(cartData) : [];
      const count = cart.length;
      
      // Update all cart badges on the page
      document.querySelectorAll('[data-cart-count]').forEach(badge => {
        badge.textContent = count;
        badge.style.display = count > 0 ? 'flex' : 'none';
      });
    } catch (e) {
      console.error('Error updating cart badge:', e);
    }
  }
}

// Initialize all product variant tables
function initProductVariantTables() {
  const tables = document.querySelectorAll('[data-product-variant-table]');
  tables.forEach(table => {
    new ProductVariantTable(table);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductVariantTables);
} else {
  initProductVariantTables();
}

// Re-initialize on Shopify theme editor section load
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
    if (section && section.querySelector('[data-product-variant-table]')) {
      const table = section.querySelector('[data-product-variant-table]');
      new ProductVariantTable(table);
    }
  });
}
